import React from "react";

import Icon, { ShareAltOutlined } from "@ant-design/icons";
import CommentsIcon from "@src/assets/icons/comments.svg";
import InfoIcon from "@src/assets/icons/info.svg";
import TargetIcon from "@src/assets/icons/target.svg";
import { CommentsOverview } from "@src/components/comments_overview/index";
import { RoleRequestBaseModal } from "@src/components/modals/role_request_modal";
import { REQUEST_STATUS, ROLES } from "@src/constants";
import { ProposeSolution } from "@src/features/staffing_request_details/components/propose_solution_tab";
import { StaffingRequestModalActionHandlers } from "@src/features/staffing_request_details/components/staffing_request_modal_action_handlers";
import { StaffingRequestOverviewTab } from "@src/features/staffing_request_details/components/staffing_request_overview_tab";
import {
  setActiveTabKey,
  setDetailsModalVisible,
} from "@src/services/slices/requestOverviewSlice";
import { useGetNoteQuery } from "@src/services/apis/notesApi";
import { useGetProjectsByResourcePlanIdQuery } from "@src/services/apis/projectsApi";
import { useGetTeamLeadRequestDetailsQuery } from "@src/services/apis/teamLeadsApi";
import { RootState, useAppSelector } from "@src/setupStore";
import { CustomTabItem } from "@src/types/antd_types";
import { DigixProject } from "@src/types/project_types";
import { Tooltip } from "antd";
import { useDispatch } from "react-redux";

import { DigiXDetailsTab } from "../digix_details_tab";

import "./staffing_request_details.less";
import {
  checkIsProposedRequestUpdated,
  checkIsDeclinedRequestUpdated,
} from "@src/features/staffing_request_details/utils/staffing_request_details_utils";

const StaffingRequestDetails: React.FC = () => {
  const dispatch = useDispatch();

  // States
  const { selectedRequestId, modalSettings } = useAppSelector(
    (state: RootState) => state.requestOverviewSlice
  );

  // RTK Queries
  const { data: request, isLoading: isRequestLoading } =
    useGetTeamLeadRequestDetailsQuery(selectedRequestId, {
      skip: !selectedRequestId,
    });

  const { data: projects } = useGetProjectsByResourcePlanIdQuery(
    request?.projectId,
    {
      skip: !request?.projectId,
    }
  );
  const isDigixProjectsTabDisabled = !projects?.length || projects.length > 1;

  const { data: notes } = useGetNoteQuery(selectedRequestId, {
    skip: !selectedRequestId,
  });

  /**
   * Function to check if the proposed tabs should be hidden. If the request is in one of the following statuses,
   * the proposed tabs should be hidden or if the request is outdated.
   *
   * @param status - The status of the request
   */
  function areProposedTabsHidden(status: string): boolean {
    const isDisabledBecauseOfStatus = [
      REQUEST_STATUS.PROPOSED,
      REQUEST_STATUS.CONFIRMED,
      REQUEST_STATUS.IN_RECRUITMENT,
      REQUEST_STATUS.DECLINED,
    ].includes(status);

    return isDisabledBecauseOfStatus || request?.isOutdated;
  }

  const tabs: CustomTabItem[] = [
    {
      key: "0",
      icon: <Icon component={InfoIcon} />,
      label: `Overview`,
      children: (
        <StaffingRequestOverviewTab
          request={request}
          isLoading={isRequestLoading}
        />
      ),
    },
    {
      key: "1",
      icon: <Icon component={TargetIcon} />,
      label: (
        <Tooltip
          data-testid="propose-solution-info-tooltip"
          title={getProposeSolutionTooltip()}
        >
          Propose Solution
        </Tooltip>
      ),
      disabled:
        checkIsProposedRequestUpdated(request) ||
        checkIsDeclinedRequestUpdated(request),
      children: <ProposeSolution status={request?.status} />,
      isHidden: areProposedTabsHidden,
    },
    {
      key: "2",
      icon: <Icon component={CommentsIcon} />,
      label: `Comments (${notes?.length})`,
      children: <CommentsOverview requestId={request?.projectRoleRequestId} />,
    },
    {
      key: "3",
      icon: <ShareAltOutlined />,
      label: (
        <Tooltip
          data-testid="digix-info-tooltip"
          title={getProjectTooltip(projects)}
        >
          DigiX Details
        </Tooltip>
      ),
      children: (
        <DigiXDetailsTab digiXProject={projects?.[0]} request={request} />
      ), // for now we know that there is only one project
      disabled: isDigixProjectsTabDisabled,
    },
  ];

  function getProjectTooltip(projects?: DigixProject[]) {
    if (!projects?.length) return "This request is not linked to any project";
    if (projects.length > 1)
      return "This request is linked to more than one project, Dualis currently only supports viewing one project";
    return "";
  }

  function getProposeSolutionTooltip() {
    if (
      checkIsProposedRequestUpdated(request) ||
      checkIsDeclinedRequestUpdated(request)
    )
      return "Propose Solution tab is currently disabled. Please take a moment to confirm if your earlier proposal is still valid.";

    return "";
  }

  return (
    <RoleRequestBaseModal
      activeTabKey={modalSettings.activeTabKey}
      handleClose={() => dispatch(setDetailsModalVisible(false))}
      handleTabChange={(key: string) => dispatch(setActiveTabKey(key))}
      modalVisible={modalSettings.detailsModalVisible}
      actions={<StaffingRequestModalActionHandlers request={request} />}
      tabs={tabs}
      requestId={request?.projectRoleRequestId}
      requestStatus={request?.childRequest?.status ?? request?.status}
      standardRoleName={request?.genericRole}
      isRequestOutdated={
        request?.childRequest?.isOutdated ?? request?.isOutdated
      }
      userRole={ROLES.TEAM_LEAD}
    />
  );
};

export default StaffingRequestDetails;
